import { LazyIssueApp } from '@jira/issue__lazy-app/src/LazyIssueApp.tsx';
import { MenuIdProjects } from '@jira/navigation-apps__constants/src/MenuIdProjects.tsx';
import { LazyHorizontalNavQueueForPaint } from '@jira/navigation-apps__horizontal-nav-jsm-queue/src/async-for-paint.tsx';
import { AsyncHorizontalNavQueue } from '@jira/navigation-apps__horizontal-nav-jsm-queue/src/async.tsx';
import { getAIContextPanelResources } from '@jira/router-resources__service-desk-ai-context-panel/src/getAIContextPanelResources.tsx';
import { getResolutionPlanSSRResource } from '@jira/router-resources__service-desk-ai-context-panel/src/ResolutionPlanSSRResource.tsx';
import { getConsolidationStateResource } from '@jira/router-resources__eoc-tenant-info/src/getConsolidationStateResource.tsx';
import { LazyAtlassianNavigation } from '@jira/navigation-apps__atlassian-navigation/src/async.tsx';
import { embeddedIssuePreloadLayoutBuilder } from '@jira/platform__layouts-issue-preload-layout/src/index.tsx';
import { getIssueViewResources } from '@jira/router-resources__issue-view/src/getIssueViewResources.tsx';
import { horizontalNavQueueResource } from '@jira/navigation-apps__resources/src/controllers/horizontal-navigation/horizontal-nav-queue/index.tsx';
import { createEntry } from '@jira/platform__routing-route-entry/src/createEntry.tsx';
import { composeLayouts } from '@jira/platform__layouts-route-layout/src/GlobalComponent.tsx';
import {
	getInsightResources,
	getIssueViewResources as getIssueViewResourcesServiceDesk,
	getKnowledgeBaseResources,
	getQueuesResources,
	getTriageButtonPermissionResource,
} from '@jira/router-resources__service-desk-common/src/getServiceDeskSettingsResources.tsx';
import { issueListSsrResource } from '@jira/router-resources__service-desk-queues/src/queuesNavigationItems.tsx';
import { queuesJqlBuilderHydrationQueryRelayResource } from '@jira/router-resources__service-desk-queues/src/queuesJqlBuilderHydrationQueryRelayResource.tsx';
import { queuesJqlBuilderAiQueryRelayResource } from '@jira/router-resources__service-desk-queues/src/queuesJqlBuilderAiQueryRelayResource.tsx';
import { resourceWithCondition } from '@jira/router-resources__utils/src/resourceWithCondition.tsx';
import { customPracticeQueueRoute } from '@jira/router-routes__servicedesk-queue-routes/src/customPracticeQueueRoute.tsx';
import { serviceProjectLayout } from '@jira/platform__layouts-service-project-layout/src/index.tsx';
import { getDraftReplyPillsResource } from '@jira/router-resources__service-desk-draft-reply/src/getDraftReplyPillsResource.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import type { Route } from '@atlassian/react-resource-router';
import {
	CustomQueueRouteComponent,
	LazyServicedeskQueuesAgentAndIssueView,
} from './ui/CustomQueueRouteComponent.tsx';
import { QueueLoadingSkeleton } from './ui/queue/index.tsx';

export const customPracticeQueueRouteEntry: Route = createEntry(customPracticeQueueRoute, () => ({
	group: 'servicedesk',
	// It is important to keep this component consistent across the two routes so
	// that React doesn't unmount it when switching between the routes and the
	// full state is kept so that scroll position and loaded data is kept when
	// going back from issue details to the queues page.
	// eslint-disable-next-line no-restricted-syntax
	component: CustomQueueRouteComponent,
	isHydrateRootEnabled: false,
	layout: composeLayouts(
		serviceProjectLayout,
		embeddedIssuePreloadLayoutBuilder([
			{
				params: [
					{
						key: 'issueKey',
					},
				],
			},
		]),
	),
	skeleton: QueueLoadingSkeleton,
	navigation: {
		menuId: MenuIdProjects,
		horizontal: AsyncHorizontalNavQueue,
	},
	resources: [
		issueListSsrResource,
		...getQueuesResources(),
		...getIssueViewResources(),
		...getKnowledgeBaseResources(),
		...getInsightResources(),
		...getTriageButtonPermissionResource(),
		getConsolidationStateResource(),
		...getAIContextPanelResources(),
		...getResolutionPlanSSRResource(),
		...getDraftReplyPillsResource(),
		horizontalNavQueueResource,
		...getIssueViewResourcesServiceDesk(),
		queuesJqlBuilderHydrationQueryRelayResource,
		resourceWithCondition(
			() => expVal('corcap-6777-ai-jqlbuilder-in-queues', 'isEnabled', false),
			queuesJqlBuilderAiQueryRelayResource,
		),
	],
	afterPaint: [LazyIssueApp],
	forPaint: [
		LazyAtlassianNavigation,
		LazyServicedeskQueuesAgentAndIssueView,
		LazyHorizontalNavQueueForPaint,
	],
	supportsPreviewPanel: true,
	bifrost: {
		bifrostGroup: 'jira-service-management',
		manifestName: 'jira-spa-jira-service-management.json',
		fetchTimeout: 1500,
	},
}));
